import { useStorage } from '@btcdirect/shared/src/composables/util/storage';
import { StorageKeys } from '@btcdirect/shared/src/composables/util/storage-keys';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export interface Authentication {
    authenticated: boolean;
    initialized: boolean;
}

export interface ClientAuthentication extends Authentication {
    token: string;
}

export interface UserAuthentication extends Authentication {
    tokens: UserTokens;
}

interface UserTokens {
    authorization?: string;
    refresh?: string;
    identifier?: string;
}

interface OneTimeAuthentication {
    token: string;
    uuid: string;
}

const { get, set, remove } = useStorage();

export const useAuthenticationStore = defineStore('authentication', () => {
    /**
     * Client
     */
    const client = ref<ClientAuthentication>({
        authenticated: false,
        initialized: false,
        token: null
    });

    function setClientAuthentication(authenticated: boolean, initialized: boolean = true): void {
        client.value.authenticated = authenticated;
        client.value.initialized = initialized;
    }

    function setClientToken(token: string): void {
        client.value.token = token ?? null;

        set(StorageKeys.CLIENT_TOKEN, token ?? null);
    }

    /**
     * User
     */
    const user = ref<UserAuthentication>({
        authenticated: false,
        initialized: false,
        tokens: {
            authorization: get(StorageKeys.AUTHORIZATION_TOKEN) ?? null,
            refresh: get(StorageKeys.REFRESH_TOKEN) ?? null,
            identifier: null
        }
    });

    const oneTimeAuthentication = ref<OneTimeAuthentication>({
        token: null,
        uuid: null
    });

    function setUserAuthentication(authenticated: boolean, initialized: boolean = true): void {
        user.value.authenticated = authenticated;
        user.value.initialized = initialized;
    }

    function setUserTokens(tokens: UserTokens): void {
        if ('authorization' in tokens) {
            user.value.tokens.authorization = tokens.authorization;
            !!tokens.authorization ? set(StorageKeys.AUTHORIZATION_TOKEN, tokens.authorization) : remove(StorageKeys.AUTHORIZATION_TOKEN);
        }

        if ('refresh' in tokens) {
            user.value.tokens.refresh = tokens.refresh;
            !!tokens.refresh ? set(StorageKeys.REFRESH_TOKEN, tokens.refresh) : remove(StorageKeys.REFRESH_TOKEN);
        }

        if ('identifier' in tokens) {
            user.value.tokens.identifier = tokens.identifier;
        }
    }

    function setOneTimeAuthentication(token: string, uuid: string): void {
        if (!token || !uuid) {
            oneTimeAuthentication.value.token = null;
            oneTimeAuthentication.value.uuid = null;
            return;
        }

        oneTimeAuthentication.value.token = token;
        oneTimeAuthentication.value.uuid = uuid;
    }

    return {
        client: computed<ClientAuthentication>(() => client.value),
        user: computed<UserAuthentication>(() => user.value),
        oneTimeAuthentication: computed<OneTimeAuthentication>(() => oneTimeAuthentication.value),
        setClientAuthentication,
        setClientToken,
        setUserAuthentication,
        setUserTokens,
        setOneTimeAuthentication
    };
});
